// ST-1705 -Claude- Orders view for online and store fulfilment.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { isNearrmeAdminRole, listCompanies, listOrders } from '../api';
import { useAdminAuth } from '../useAdminAuth';
import { nextSort, SortHeader, sortRows, SortState } from '../tableSort';

export function AdminOrdersPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);
  const [companyCode, setCompanyCode] = useState(isNearrmeAdmin ? '__all' : user?.companyCode ?? '');
  const [mode, setMode] = useState<'all' | 'online' | 'store'>('all');
  const [sort, setSort] = useState<SortState<'order' | 'status' | 'product' | 'store' | 'customer' | 'total' | 'warranty'>>({
    key: 'order',
    direction: 'desc',
  });

  const companies = useQuery({ queryKey: ['admin', 'companies'], queryFn: listCompanies });
  const orders = useQuery({
    queryKey: ['admin', 'orders', companyCode],
    queryFn: () => listOrders(companyCode),
    enabled: !!companyCode,
  });

  const filtered = (orders.data ?? []).filter((order) => {
    if (mode === 'online') return order.orderChannel === 'Online Order' || order.fulfilmentMode === 'MailDirect';
    if (mode === 'store') return order.orderChannel === 'Store Order' || order.fulfilmentMode !== 'MailDirect';
    return true;
  });
  const sorted = sortRows(filtered, sort, {
    order: (order) => order.createdAtUtc,
    status: (order) => order.orderStatus,
    product: (order) => order.productName,
    store: (order) => order.storeName ?? order.companyCode,
    customer: (order) => order.consumerName ?? order.consumerEmail,
    total: (order) => order.totalInr,
    warranty: (order) => order.warrantyRecordId,
  });
  const sortHeader = (label: string, key: typeof sort.key) => (
    <SortHeader label={label} sortKey={key} sort={sort} onSort={(nextKey) => setSort((current) => nextSort(current, nextKey))} />
  );

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Orders</h1>
      <p className="mt-1 text-slate-600 dark:text-slate-400">
        Online orders, store orders, store details, customer details, warranty, and attribution.
      </p>

      <Card padded className="mt-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Company</label>
            <select
              value={companyCode}
              onChange={(e) => setCompanyCode(e.target.value)}
              disabled={!isNearrmeAdmin}
              className="h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm disabled:opacity-70"
            >
              {isNearrmeAdmin && <option value="__all">All companies</option>}
              {(companies.data ?? []).map((c) => (
                <option key={c.companyCode ?? ''} value={c.companyCode ?? ''}>
                  {c.companyCode} - {c.companyName}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Order type</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as typeof mode)}
              className="h-9 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
            >
              <option value="all">All orders</option>
              <option value="online">Online only</option>
              <option value="store">Store orders</option>
            </select>
          </div>
        </div>
      </Card>

      {orders.isLoading && (
        <div className="mt-4 space-y-2">
          {[1, 2, 3, 4].map((i) => <LoadingSkeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {orders.error && (
        <Card padded className="mt-4">
          <div className="text-sm text-red-600 dark:text-red-400">{(orders.error as Error).message}</div>
        </Card>
      )}

      {orders.data && (
        <>
          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {filtered.length} {filtered.length === 1 ? 'order' : 'orders'}
          </div>
          {filtered.length === 0 ? (
            <EmptyState title="No orders" description="No order data is available for this filter yet." />
          ) : (
            <Card padded={false} className="mt-2 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Order', 'order')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Status', 'status')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Product', 'product')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Store', 'store')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Customer', 'customer')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Total', 'total')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Warranty', 'warranty')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {sorted.map((order) => (
                      <tr
                        key={order.consumerOrderId}
                        onClick={() => navigate(`/admin/orders/${order.consumerOrderId}`)}
                        tabIndex={0}
                        role="link"
                        className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:hover:bg-slate-900 dark:focus:bg-slate-900"
                      >
                        <td className="px-4 py-3">
                          <div className="font-mono text-xs text-slate-500">#{order.consumerOrderId}</div>
                          <div className="mt-1 text-xs font-semibold text-brand-700 dark:text-brand-300">{order.orderChannel ?? 'Store Order'}</div>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{order.serviceMethod ?? order.fulfilmentMode}</div>
                          <div className="mt-1 text-xs text-slate-400">{new Date(order.createdAtUtc).toLocaleString()}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                            {order.orderStatus ?? 'Pending'}
                          </span>
                          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{order.fulfilmentMode}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 dark:text-slate-100">{order.productName}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{order.brandName ?? 'Brand pending'}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">Qty {order.quantity}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          <div>{order.storeName ?? 'Online fulfilment'}</div>
                          <div className="text-xs text-slate-400">{order.companyCode}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          <div>{order.consumerName ?? 'Guest consumer'}</div>
                          <div className="text-xs text-slate-400">{order.consumerEmail ?? 'Email pending'}</div>
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">
                          Rs. {order.totalInr.toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-400">
                          {order.warrantyRecordId ?? 'Pending'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
